/**
 * Campaign Detail Page
 *
 * File: Mythbound.App/src/pages/CampaignPage.tsx
 * Created: 2026-02-26
 * Last Modified: 2026-02-26
 *
 * PURPOSE:
 * Full campaign detail page for /campaigns/:campaignId route. Displays campaign
 * info, action buttons (Play Myth, Invite Players, Settings), stat cards,
 * session zero banner (GM only), and a CharacterRosterSidebar.
 * Wires CampaignContext, CharacterContext, SessionContext, AuthContext, and
 * WebSocket hooks for real-time campaign event subscriptions.
 *
 * LAYOUT: Two-column — main content (flex:1) + CharacterRosterSidebar (right, fixed width).
 * The sidebar manages its own fixed positioning and collapsed/mobile state internally.
 *
 * Pipeline: App.tsx → CampaignPage → CampaignContext/CharacterContext/SessionContext →
 *           CampaignService/CharacterService/SessionService → Supabase/FastAPI
 *
 * Dependencies:
 *   Contexts: CampaignContext, CharacterContext, SessionContext, AuthContext
 *   Hooks: useCampaignWebSocket, useSessionWebSocket, useCharacterWebSocket
 *   Components: CharacterCreationModal, CharacterRosterSidebar,
 *               CharacterVisibilitySettings, InviteLinkModal
 *   Route param: campaignId (string, format: camp_XXXXXX)
 *
 * PROP CONTRACT NOTES (verified from source files 2026-02-26):
 *   CharacterRosterSidebar props:
 *     - campaignId: string          (required)
 *     - isGM: boolean               (required)
 *     - currentUserId?: string      (optional)
 *     - onCharacterClick?: (character: Character) => void
 *     - onAddCharacterClick?: () => void
 *     - onVisibilitySettingsClick?: (character: Character) => void
 *   CharacterCreationModal props:
 *     - campaignId: string          (required)
 *     - isOpen: boolean             (required)
 *     - onClose: () => void         (required)
 *     - onCharacterCreated?: (character: Character) => void
 *   CharacterVisibilitySettings props:
 *     - character: Character        (required)
 *     - isOpen: boolean             (required)
 *     - onClose: () => void         (required)
 *     - isGM?: boolean              (optional, defaults false)
 *   InviteLinkModal props:
 *     - campaignId: string          (required)
 *     - campaignName: string        (required)
 *     - isOpen: boolean             (required)
 *     - onClose: () => void         (required)
 *
 * WebSocket hook signatures (verified 2026-02-26):
 *   useCampaignWebSocket(campaignId?: string)
 *   useSessionWebSocket(campaignId?: string)
 *   useCharacterWebSocket(campaignId?: string)
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router';
import {
  Box,
  Flex,
  Heading,
  Text,
  Button,
  SimpleGrid,
  Spinner,
  HStack,
  useToast,
} from '@chakra-ui/react';

// Contexts
import { useCampaign } from '../context/CampaignContext';
// Added 2026-02-26: useCharacter EXISTS in CharacterContext (confirmed — exports
// activeCharacter, roster, loadRoster, setActiveCharacter, etc.) and is used
// here for roster.length in the stat card and for forwarding to modal callbacks.
import { useCharacter } from '../context/CharacterContext';
import { useSession } from '../context/SessionContext';
import { useOptionalCampaignRealtime } from '../context/CampaignRealtimeContext';
import { getSessionErrorCode } from '../services/api/SessionService';
import { useAuth } from '../context/AuthContext';

// WebSocket hooks — fire-and-forget subscriptions; update contexts automatically
import { useCampaignWebSocket } from '../hooks/useCampaignWebSocket';
import { useSessionWebSocket } from '../hooks/useSessionWebSocket';
import { useCharacterWebSocket } from '../hooks/useCharacterWebSocket';

// Campaign components — all are named exports from the barrel index
import {
  CharacterCreationModal,
  CharacterRosterSidebar,
  CharacterVisibilitySettings,
  InviteLinkModal,
} from '../components/campaign';

// Character type for modal callbacks
import type { Character } from '../services/api/CharacterService';

// Added 2026-09-21 (PRD001A 001A.4 Wave 3): shared unavailable-state panel —
// reused here (not a new parallel UI) for the roster's 'unavailable' status.
import { ServiceUnavailable } from '../components/common/ServiceUnavailable';
import { CampaignContentSummary } from '../components/campaign/CampaignContentSummary';

const CampaignPage: React.FC = () => {
  const { campaignId } = useParams<{ campaignId: string }>();
  const navigate = useNavigate();
  const toast = useToast();

  // --- Contexts ---
  const campaignState = useCampaign();
  const { loadCampaign, currentCampaign, campaignError, campaignErrorKind, participantsError } = campaignState;
  // List refreshes must not hide or clear the currently requested detail state.
  const campaignLoading = campaignState.isCampaignLoading;
  // Added 2026-02-26: useCharacter confirmed present; roster.length drives the
  // Characters stat card and setActiveCharacter is forwarded via onCharacterClick.
  // Updated 2026-09-21 (PRD001A 001A.4 Wave 3): also read the structured
  // roster status/message — see the render guards below and CharacterContext's
  // module header for why 'forbidden'/'unavailable' must render distinctly
  // from an empty roster rather than all collapsing to the same thing.
  const {
    roster,
    loadRoster,
    setActiveCharacter,
    status: characterStatus,
    message: characterMessage,
  } = useCharacter();
  const { startSession, isActive: sessionActive, isLoading: sessionLoading } = useSession();
  const realtime = useOptionalCampaignRealtime();
  const campaignSnapshot = realtime?.snapshot;
  const campaignCursorKnown = campaignSnapshot !== null && campaignSnapshot !== undefined &&
    campaignSnapshot.campaign.campaign_id === campaignId &&
    typeof campaignSnapshot.campaign.is_session_active === 'boolean' &&
    (campaignSnapshot.campaign.current_session_id === null || typeof campaignSnapshot.campaign.current_session_id === 'string');
  const { userInfo } = useAuth();

  // --- WebSocket subscriptions (fire-and-forget, update contexts in real-time) ---
  useCampaignWebSocket(campaignId ?? '');
  useSessionWebSocket(campaignId ?? '');
  useCharacterWebSocket(campaignId ?? '');

  // Added 2026-02-26: GM check — compare authenticated user ID against campaign creator.
  const isGM = !!(userInfo?.id && currentCampaign?.creator_user_id === userInfo.id);

  // --- Modal state ---
  const [isCharacterCreateOpen, setIsCharacterCreateOpen] = useState(false);
  const [isInviteLinkOpen, setIsInviteLinkOpen] = useState(false);
  const [isVisibilitySettingsOpen, setIsVisibilitySettingsOpen] = useState(false);
  // Added 2026-02-26: Tracks which character's visibility settings are being edited.
  const [selectedCharacterForSettings, setSelectedCharacterForSettings] =
    useState<Character | null>(null);

  // --- Load campaign and roster on mount / route param change ---
  useEffect(() => {
    if (campaignId) {
      loadCampaign(campaignId);
      // Added 2026-02-26: Also load the character roster so roster.length
      // is accurate and CharacterRosterSidebar has data to draw from context.
      loadRoster(campaignId);
    }
  // A same-route account change requires a fresh authorized detail and roster.
  }, [campaignId, userInfo?.id, loadCampaign, loadRoster]);

  // Campaign realtime context accepts only validated snapshots. Its roster is
  // a privacy-safe membership projection, so use it solely as a hint to reload
  // full authorized Character records through CharacterContext.
  useEffect(() => {
    if (campaignId && campaignSnapshot?.campaign.campaign_id === campaignId) {
      void loadRoster(campaignId);
    }
  }, [campaignId, campaignSnapshot, loadRoster]);

  // --- Guard: missing route param ---
  if (!campaignId) {
    return (
      <Flex minH="60vh" align="center" justify="center">
        <Text color="#C5C5C7">No campaign ID specified. Please select a campaign.</Text>
      </Flex>
    );
  }

  // Never render a campaign whose identity does not match the current URL,
  // including the render before the route effect starts its next request.
  if (campaignLoading || currentCampaign?.campaign_id !== campaignId) {
    if (campaignError && !campaignLoading) {
      if (campaignErrorKind === 'unavailable' || campaignErrorKind === 'network') {
        return (
          <ServiceUnavailable
            title="Campaign unavailable"
            description={campaignError}
            onRetry={() => void loadCampaign(campaignId)}
          />
        );
      }
      const title = campaignErrorKind === 'forbidden' || campaignErrorKind === 'unauthenticated'
        ? 'Access restricted'
        : campaignErrorKind === 'not_found' ? 'Campaign not found' : 'Campaign unavailable';
      return (
        <Flex minH="60vh" align="center" justify="center" direction="column" gap={3}>
          <Text color="#C5C5C7" fontWeight="bold" fontSize="lg">{title}</Text>
          <Text color="#8a8a90" textAlign="center">{campaignError}</Text>
          <Button onClick={() => void loadCampaign(campaignId)} colorScheme="yellow">Try again</Button>
        </Flex>
      );
    }
    return (
      <Flex minH="60vh" align="center" justify="center">
        <Spinner size="xl" color="#D9B555" />
      </Flex>
    );
  }

  // --- Guard: character roster unavailable/forbidden (PRD001A 001A.4 Wave 3) ---
  // Distinct from 'loading'/'empty' (handled inline in the stat card below,
  // since those don't block the rest of the page): a 503/network failure on
  // the roster fetch must render as an honest unavailable state, and a 403
  // as a distinct restricted state — neither collapses into "0 characters."
  if (characterStatus === 'unavailable') {
    return (
      <ServiceUnavailable
        title="Campaign characters unavailable"
        description={characterMessage ?? "We couldn't reach the character service for this campaign right now."}
        onRetry={() => void loadRoster(campaignId)}
      />
    );
  }

  if (characterStatus === 'forbidden') {
    return (
      <Flex minH="60vh" align="center" justify="center" direction="column" gap={2} textAlign="center" px={6}>
        <Text color="#C5C5C7" fontWeight="bold" fontSize="lg">Access restricted</Text>
        <Text color="#8a8a90" fontSize="sm" maxW="sm">
          {characterMessage ?? "You don't have permission to view this campaign's characters."}
        </Text>
      </Flex>
    );
  }

  // --- Handler: "Play Myth" button ---
  // Added 2026-02-26: Starts session via SessionContext then navigates to the
  // session view. Errors surface as Chakra toast (not console.error silently).
  const handleStartSession = async () => {
    try {
      const session = await startSession(campaignId, 'webapp');
      navigate(`/session/${session.session_id}`);
    } catch (err) {
      if (getSessionErrorCode(err) === 'stale_state') {
        realtime?.refreshSnapshot();
        toast({
          title: 'Campaign state changed',
          description: 'The current campaign state is refreshing. Review it, then choose Play Myth again.',
          status: 'warning',
        });
      } else {
        toast({ title: err instanceof Error ? err.message : 'Failed to start session', status: 'error' });
      }
    }
  };

  // --- Handler: character click in roster sidebar ---
  // Added 2026-02-26: Sets the clicked character as active in CharacterContext.
  const handleCharacterClick = (character: Character) => {
    setActiveCharacter(character);
    // TODO: Show character sheet in main view or modal (Phase 2)
  };

  // --- Handler: visibility settings icon click in roster sidebar ---
  // Added 2026-02-26: Opens CharacterVisibilitySettings for the selected character.
  const handleVisibilitySettingsClick = (character: Character) => {
    setSelectedCharacterForSettings(character);
    setIsVisibilitySettingsOpen(true);
  };

  // --- Handler: character successfully created in modal ---
  const handleCharacterCreated = (_character: Character) => {
    // The modal creates through CharacterService directly; reload the same
    // authorized campaign roster so both the stat and sidebar use the server's
    // complete projection instead of relying on a later socket event.
    void loadRoster(campaignId);
    // TODO: Show toast "Character created successfully!" (Phase 2)
  };

  return (
    <>
      {/* Two-column wrapper: main content left, CharacterRosterSidebar fixed right */}
      <Flex minH="100vh" bg="#111116">

        {/* ------------------------------------------------------------------ */}
        {/* MAIN CONTENT COLUMN                                                 */}
        {/* Added 2026-02-26: marginRight reserves space for the 280px sidebar  */}
        {/* on desktop so content doesn't slide under the fixed sidebar.        */}
        {/* ------------------------------------------------------------------ */}
        <Box flex={1} p={8} mr={{ base: 0, md: '280px' }}>
          {participantsError && (
            <Flex mb={4} align="center" gap={3} role="status">
              <Text color="#C5C5C7">Campaign details loaded, but the member list is unavailable: {participantsError}</Text>
              <Button size="sm" onClick={() => void loadCampaign(campaignId)}>Try again</Button>
            </Flex>
          )}

          {/* 1. Campaign header -------------------------------------------- */}
          <Heading
            color="#D9B555"
            fontFamily="Cinzel, serif"
            mb={2}
            fontSize={{ base: '2xl', md: '4xl' }}
          >
            {currentCampaign.campaign_name}
          </Heading>
          <Text color="#C5C5C7" mb={6} lineHeight={1.6}>
            {currentCampaign.description}
          </Text>

          {/* 004.6: read the persisted campaign_settings projection returned by
              CampaignService. No builder-local state is trusted across navigation. */}
          <CampaignContentSummary campaignSettings={currentCampaign.campaign_settings} />

          {/* 2. Action buttons --------------------------------------------- */}
          <HStack mb={8} spacing={4} flexWrap="wrap">
            {/* Play Myth — primary action; disabled when session already active */}
            <Button
              bg="#D9B555"
              color="#111116"
              fontWeight="bold"
              fontFamily="Cinzel, serif"
              isDisabled={sessionActive || sessionLoading || !campaignCursorKnown || Boolean(realtime?.snapshot?.campaign.is_session_active)}
              isLoading={sessionLoading}
              loadingText="Starting..."
              onClick={handleStartSession}
              _hover={{ bg: '#c9a545' }}
            >
              Play Myth
            </Button>
            {!campaignCursorKnown && (
              <Text role="status" color="#C5C5C7" fontSize="sm">
                Confirming current campaign state before enabling session start.
              </Text>
            )}

            {/* Invite Players — GM only */}
            {isGM && (
              <Button
                variant="outline"
                borderColor="#D9B555"
                color="#D9B555"
                _hover={{ bg: 'rgba(217,181,85,0.08)' }}
                onClick={() => setIsInviteLinkOpen(true)}
              >
                Invite Players
              </Button>
            )}

            {/* Visibility Settings — GM only */}
            {isGM && (
              <Button
                variant="outline"
                borderColor="#C5C5C7"
                color="#C5C5C7"
                _hover={{ bg: 'rgba(197,197,199,0.08)' }}
                onClick={() => setIsVisibilitySettingsOpen(true)}
              >
                Settings
              </Button>
            )}
          </HStack>

          {/* 3. Stat cards grid -------------------------------------------- */}
          <SimpleGrid columns={{ base: 1, sm: 2, md: 3 }} spacing={4} mb={8}>
            <Box bg="#1a1a20" borderRadius="md" p={4}>
              <Text color="#C5C5C7" fontSize="sm" mb={1}>Game System</Text>
              <Text color="#D9B555" fontWeight="bold">
                {/* Added 2026-02-26: Optional chaining guards if game_system is null */}
                {currentCampaign.game_system ?? 'Not set'}
              </Text>
            </Box>

            <Box bg="#1a1a20" borderRadius="md" p={4}>
              <Text color="#C5C5C7" fontSize="sm" mb={1}>Participants</Text>
              <Text color="#D9B555" fontWeight="bold">
                {/* Added 2026-02-26: Optional chaining guards partial Campaign data */}
                {currentCampaign.participant_count ?? 0}
                {' / '}
                {currentCampaign.max_participants ?? '∞'}
              </Text>
            </Box>

            <Box bg="#1a1a20" borderRadius="md" p={4}>
              <Text color="#C5C5C7" fontSize="sm" mb={1}>Characters</Text>
              {/* Added 2026-02-26: useCharacter exists — use roster.length for live count.
                  Updated 2026-09-21 (PRD001A 001A.4 Wave 3): loading/empty render
                  distinctly instead of both looking like "0 Active". */}
              {characterStatus === 'loading' ? (
                <Spinner size="sm" color="#D9B555" />
              ) : characterStatus === 'empty' ? (
                <Text color="#8a8a90" fontWeight="bold">No characters yet</Text>
              ) : (
                <Text color="#D9B555" fontWeight="bold">{roster.length} Active</Text>
              )}
            </Box>
          </SimpleGrid>

          {/* 4. Session Zero banner (GM only, if not completed) ------------- */}
          {/* Added 2026-02-26: Optional chaining on session_zero_config because
              the Campaign type may not always include this field at runtime.   */}
          {isGM && !currentCampaign.session_zero_config?.completed && (
            <Box
              bg="#1a1a20"
              border="1px solid #35889A"
              borderRadius="md"
              p={4}
              mb={8}
            >
              <Text color="#35889A" fontWeight="bold" mb={1}>
                Session Zero Incomplete
              </Text>
              <Text color="#C5C5C7" fontSize="sm" mb={3}>
                Complete your Session Zero to unlock full campaign features.
              </Text>
              <Button
                size="sm"
                variant="outline"
                borderColor="#35889A"
                color="#35889A"
                _hover={{ bg: 'rgba(53,136,154,0.1)' }}
              >
                Configure Session Zero
              </Button>
            </Box>
          )}

          {/* 5. Placeholder for future content ------------------------------ */}
          {/* [FUTURE CODESPACE] Session content and gameplay tools (Phase 2) */}
          <Box
            border="1px dashed #333"
            borderRadius="md"
            p={8}
            textAlign="center"
          >
            <Text color="#C5C5C7" fontSize="sm">
              Session content and gameplay tools are coming soon.
            </Text>
          </Box>

        </Box>

        {/* ------------------------------------------------------------------ */}
        {/* RIGHT SIDEBAR — CharacterRosterSidebar                             */}
        {/* Added 2026-02-26: Sidebar is position:fixed internally (verified    */}
        {/* in source); we pass campaignId + isGM + all three event handlers.   */}
        {/* The sidebar starts open (not collapsed) by default internally.      */}
        {/* ------------------------------------------------------------------ */}
        <CharacterRosterSidebar
          campaignId={campaignId}
          isGM={isGM}
          currentUserId={userInfo?.id}
          characters={roster}
          isLoading={characterStatus === 'loading' || characterStatus === 'idle'}
          error={['forbidden', 'not_found', 'unavailable', 'error'].includes(characterStatus) ? characterMessage : null}
          onRetry={() => void loadRoster(campaignId)}
          onCharacterClick={handleCharacterClick}
          onAddCharacterClick={() => setIsCharacterCreateOpen(true)}
          onVisibilitySettingsClick={handleVisibilitySettingsClick}
        />

      </Flex>

      {/* -------------------------------------------------------------------- */}
      {/* MODALS                                                                */}
      {/* -------------------------------------------------------------------- */}

      {/* Character Creation Modal */}
      <CharacterCreationModal
        campaignId={campaignId}
        isOpen={isCharacterCreateOpen}
        onClose={() => setIsCharacterCreateOpen(false)}
        onCharacterCreated={handleCharacterCreated}
      />

      {/* Character Visibility Settings Modal
          Added 2026-02-26: Only mount when a character is selected to avoid
          passing undefined to the required `character` prop. */}
      {selectedCharacterForSettings && (
        <CharacterVisibilitySettings
          character={selectedCharacterForSettings}
          isOpen={isVisibilitySettingsOpen}
          onClose={() => {
            setIsVisibilitySettingsOpen(false);
            setSelectedCharacterForSettings(null);
          }}
          isGM={isGM}
        />
      )}

      {/* Invite Link Modal — GM only, requires campaignName from currentCampaign */}
      <InviteLinkModal
        campaignId={campaignId}
        campaignName={currentCampaign.campaign_name}
        isOpen={isInviteLinkOpen}
        onClose={() => setIsInviteLinkOpen(false)}
      />
    </>
  );
};

export default CampaignPage;
